import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import EntityNameSchema from "legal-entities/entity-names/_schema/entity-name-schema";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import RegistrationInfoSchema, {
  RegistrationInfoJSON,
} from "legal-entities/registration-info/_schema/registration-info-schema";
import RegistrationInfo from "legal-entities/registration-info/registration-info";
import UserInformationSchema, { UserInformationParams } from "users/information/_schemas/user-information-schema";
import UserEntityInfo from "users/information/entity-information/user-entity-info";
import UserEntityInfoSchema, { UserEntityInfoJSON } from "users/information/entity-information/user-entity-info-schema";
import { z } from "zod";

export type UserEntityMemberInvitationJSON = z.infer<
  typeof UserEntityMemberInvitationSchema.UserEntityMemberInvitationJSONZ
>;
export type UserEntityMemberInvitationParams = z.infer<
  typeof UserEntityMemberInvitationSchema.UserEntityMemberInvitationZ
>;

export default class UserEntityMemberInvitationSchema {
  public static readonly UserEntityMemberInvitationJSONZ = z.object({
    id: GuidSchema.GuidJSONZ.nullish(),
    entityName: EntityNameSchema.EntityNameJSONZ,
    legalEntityId: GuidSchema.GuidJSONZ,
    user: UserInformationSchema.UserInformationJSONZ,
    status: z.enum(InvitationStatus).nullish(),
  });

  public static readonly UserEntityMemberInvitationZ = z.object({
    id: z.instanceof(Guid).optional(),
    userEntityInfo: z.instanceof(UserEntityInfo),
    invitedEmail: z.instanceof(EmailAddress),
    registrationInfo: z.instanceof(RegistrationInfo),
    status: z.enum(InvitationStatus).optional(),
  });

  public static validate(values: unknown): UserEntityMemberInvitationParams {
    return parseOrThrow(UserEntityMemberInvitationSchema.UserEntityMemberInvitationZ, values, {
      logError: true,
      schemaName: "UserEntityMemberInvitationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): UserEntityMemberInvitationJSON {
    return parseOrThrow(UserEntityMemberInvitationSchema.UserEntityMemberInvitationJSONZ, json, {
      logError: true,
      schemaName: "UserEntityMemberInvitationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is UserEntityMemberInvitationParams {
    try {
      UserEntityMemberInvitationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is UserEntityMemberInvitationJSON {
    try {
      UserEntityMemberInvitationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): UserEntityMemberInvitationParams {
    const parsed = parseOrThrow(UserEntityMemberInvitationSchema.UserEntityMemberInvitationJSONZ, json, {
      logError: true,
      schemaName: "UserEntityMemberInvitationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return UserEntityMemberInvitationSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: UserEntityMemberInvitationParams): UserEntityMemberInvitationJSON {
    const validated = UserEntityMemberInvitationSchema.validate(legalEntity);
    return UserEntityMemberInvitationSchema.codec.encode(validated);
  }

  private static readonly codec = z.codec(
    UserEntityMemberInvitationSchema.UserEntityMemberInvitationJSONZ,
    UserEntityMemberInvitationSchema.UserEntityMemberInvitationZ,
    {
      decode: (json) =>
        ({
          id: new Guid(GuidSchema.deserialize(json.id)),
          userEntityInfo: UserEntityInfo.create(
            UserEntityInfoSchema.deserialize({
              ...json,
              isAdmin: false,
              isManager: false,
              isOfficer: false,
              entityId: json.legalEntityId,
            } satisfies UserEntityInfoJSON),
          ),
          invitedEmail: new EmailAddress(json.user.email!),
          registrationInfo: new RegistrationInfo(
            RegistrationInfoSchema.deserialize({
              ...json,
              name: {
                firstName: json.user.firstName,
                lastName: json.user.lastName,
              },
              email: json.user.email!,
            } satisfies RegistrationInfoJSON),
          ),
          status: json.status ?? undefined,
        }) satisfies UserEntityMemberInvitationParams,
      encode: (params) => ({
        id: params.id?.toJSON(),
        legalEntityId: params.userEntityInfo.id.toJSON(),
        entityName: params.userEntityInfo.entityName.toJSON(),
        user: UserInformationSchema.serialize({
          id: params.id ?? Guid.EMPTY,
          email: params.invitedEmail,
          legalEntityId: params.userEntityInfo.id,
          companyName: params.userEntityInfo.entityName.value,
          name: params.registrationInfo.name,
          isVendor: params.userEntityInfo.entityVendorFlag ?? false,
          isClient: params.userEntityInfo.entityClientFlag ?? false,
          isVendorRepresentative: false,
          isClientRepresentative: false,
          viewingAsVendor: params.userEntityInfo.entityVendorFlag ?? false,
          isSuperUser: false,
        } satisfies UserInformationParams),
        status: params.status,
      }),
    },
  );
}
